import { ClaimsSection, useClaims } from '@/features/claims';
import { PolicyholderSection, PolicySearchPanel, usePolicySearch } from '@/features/policy';
import { ServiceUnavailableBanner } from '@/components/shared/ServiceUnavailableBanner';
import { isConnectivityFailure } from '@/lib/service-failure';

/**
 * The authenticated home screen (US-006 AC-001, US-009): the search panel pinned at the top, then
 * Policyholder Information, then the Claims grid — one page, fixed order, no tabs between them.
 *
 * Composition of two features lives here rather than inside either one: a feature never imports
 * another feature.
 */
export function PolicyWorkspacePage(): React.JSX.Element {
  const { error } = usePolicySearch();
  const { error: claimsError } = useClaims();

  // A policy that does not exist has no claims section to degrade: "no claims for this policy" would
  // assert a policy the search just refused. Any other failure still shows the claims section, because
  // a Claims outage and a Policy outage are independent (US-011 AC-005).
  const policyNotFound = error?.status === 404;

  // PP-028: either call failing to reach the backend is one outage, stated once at the top of the page.
  // Composed here rather than inside either feature — this is where the two calls are already known to
  // each other, and one banner per page is the whole point of standardising the message.
  const unreachable = isConnectivityFailure(error) || isConnectivityFailure(claimsError);

  return (
    <div className="flex flex-col gap-5">
      {unreachable && <ServiceUnavailableBanner />}
      <PolicySearchPanel />
      <PolicyholderSection />
      {!policyNotFound && <ClaimsSection />}
    </div>
  );
}
